// series.ts — P1.2c BEST-OF-THREE async series model (pure, framework-free, deterministic).
//
// A normal P1.2 challenge is a single board ("beat my 1200"). A *series* turns that into a 3-game,
// best-of-three duel against a friend, played ASYNCHRONOUSLY by passing a deep link back and forth.
// This module is the pure model only — no Phaser, no DOM, no Date/Math.random — so it can be unit
// tested headless and reused by the renderer + any future server re-sim. The renderer (ResultScene /
// MenuScene / GameScene / share.ts) is a pure consumer of these functions, mirroring how the rest of
// the game splits sim from view.
//
// THE PING-PONG (why there are two phases per turn)
// You can't challenge a friend to a game you haven't played, so an async turn has two halves:
//   1) RESOLVE  — you open the friend's link (it carries their score as `target`). You play the SAME
//                 board (same seed) and either beat the target (you win that game) or miss it.
//   2) SET      — if the series isn't over you play ONE fresh board on the same topic to set the next
//                 game's target, then re-share. The friend opens that link and RESOLVEs it.
// `SeriesState` is always stored from the LOCAL player's point of view (youWins = my games). When you
// re-share, `opponentSeries()` swaps the perspective so the recipient reads "you" as themselves.
//
// Standings are carried compactly in the URL as `&series=bo3 &g=<game> &y=<youWins> &t=<themWins>`
// (see registryKeys.parseBootParams + share.buildDeepLink). `game` is the 1-based game about to be
// played; `youWins`/`themWins` count games already decided.

// NOTE: parseSeries takes a STRUCTURAL subset of registryKeys.BootParams rather than importing the
// type, so this module compiles BEFORE the integration adds the series fields to BootParams (keeping
// the build green until the main loop wires it). Once registryKeys.BootParams gains seriesMode/
// seriesGame/seriesYou/seriesThem it is assignable to SeriesBootParams, so `parseSeries(readBoot(...))`
// type-checks with no extra cast.

/** The boot/registry fields parseSeries reads. registryKeys.BootParams becomes a supertype of this. */
export interface SeriesBootParams {
  /** The raw `&series=` value: 'bo3' enables the series, anything else (incl. '') means no series. */
  seriesMode: string;
  /** Raw `&g=` game number (default 1). */
  seriesGame: number;
  /** Raw `&y=` local-player wins (default 0). */
  seriesYou: number;
  /** Raw `&t=` friend wins (default 0). */
  seriesThem: number;
}

/** Only best-of-three exists today; the field future-proofs the deep-link encoding (e.g. 'bo5'). */
export type SeriesMode = 'bo3';

/** Total games in a best-of-three. */
export const SERIES_BEST_OF = 3;
/** Games needed to clinch (a strict majority of `SERIES_BEST_OF`). */
export const SERIES_CLINCH = 2;

/** The live standings of an async best-of-three, always from the LOCAL player's point of view. */
export interface SeriesState {
  mode: SeriesMode;
  /** Total games in the series (3 for bo3). */
  bestOf: number;
  /** 1-based index of the game currently being played / set (1..bestOf, may read bestOf+1 once done). */
  game: number;
  /** Games the local player has won so far. */
  youWins: number;
  /** Games the friend has won so far. */
  themWins: number;
}

/** The four compact URL params that encode a `SeriesState` (string values, ready for URLSearchParams). */
export interface SeriesLinkParams {
  series: SeriesMode;
  g: string;
  y: string;
  t: string;
}

function clampInt(value: number, lo: number, hi: number, fallback: number): number {
  const n = Math.floor(value);
  if (!Number.isFinite(n)) return fallback;
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}

/**
 * Read a `SeriesState` out of the parsed boot params (URL/registry), or `null` when there is no valid
 * series. registryKeys does only dumb string→number parsing; THIS function owns the contract: it
 * accepts the mode iff it is exactly 'bo3' and clamps the standings/game into legal ranges so a
 * hand-edited or stale link can never produce an out-of-bounds state.
 */
export function parseSeries(boot: SeriesBootParams): SeriesState | null {
  if (boot.seriesMode !== 'bo3') return null;
  const bestOf = SERIES_BEST_OF;
  const youWins = clampInt(boot.seriesYou, 0, SERIES_CLINCH, 0);
  const themWins = clampInt(boot.seriesThem, 0, SERIES_CLINCH, 0);
  // game is at least youWins+themWins+1 (you can't be on game 1 if two games are already decided),
  // capped at bestOf+1 (the "series complete" sentinel).
  const decided = youWins + themWins;
  const game = clampInt(boot.seriesGame, 1, bestOf + 1, Math.min(decided + 1, bestOf + 1));
  return { mode: 'bo3', bestOf, game, youWins, themWins };
}

/** A fresh best-of-three at game 1, 0–0 — used when the local player OPTS INTO a series from a result. */
export function startSeries(): SeriesState {
  return { mode: 'bo3', bestOf: SERIES_BEST_OF, game: 1, youWins: 0, themWins: 0 };
}

/** True once a player has clinched the majority (or every game has been played). */
export function isSeriesOver(state: SeriesState): boolean {
  return (
    state.youWins >= SERIES_CLINCH ||
    state.themWins >= SERIES_CLINCH ||
    state.youWins + state.themWins >= state.bestOf ||
    state.game > state.bestOf
  );
}

/**
 * Resolve the game the local player just finished and return the NEXT state (never mutates the input).
 * `youWonThisGame` is "did I beat the friend's target for this game?" — a tie counts as a win for the
 * resolver (>= comparison is the caller's job). `game` advances by one so the result reads as the
 * upcoming game; once the series is over `isSeriesOver` short-circuits any further play.
 */
export function advanceSeries(state: SeriesState, youWonThisGame: boolean): SeriesState {
  const youWins = state.youWins + (youWonThisGame ? 1 : 0);
  const themWins = state.themWins + (youWonThisGame ? 0 : 1);
  const game = Math.min(state.game + 1, state.bestOf + 1);
  return { mode: state.mode, bestOf: state.bestOf, game, youWins, themWins };
}

/**
 * The final verdict once the series is over: `true` you won, `false` the friend won, `null` if it is
 * not actually over yet or somehow level (defensive — a strict-majority bo3 can't truly tie).
 */
export function seriesWonByYou(state: SeriesState): boolean | null {
  if (!isSeriesOver(state)) return null;
  if (state.youWins > state.themWins) return true;
  if (state.themWins > state.youWins) return false;
  return null;
}

/** A short label for the game in play, e.g. "Game 2 of 3". */
export function seriesGameLabel(state: SeriesState): string {
  return `Game ${Math.min(state.game, state.bestOf)} of ${state.bestOf}`;
}

/**
 * One-line standings for the result card. While the series is live it reads like the brief asked for —
 * "Series 1–1, game 3"; once decided it reads "Series won 2–1" / "Series lost 1–2". The en dash (–)
 * matches the score-duel styling used elsewhere on the result card.
 */
export function seriesStandingText(state: SeriesState): string {
  const score = `${state.youWins}–${state.themWins}`;
  if (isSeriesOver(state)) {
    const verdict = seriesWonByYou(state);
    if (verdict === true) return `Series won ${score}`;
    if (verdict === false) return `Series lost ${score}`;
    return `Series tied ${score}`;
  }
  return `Series ${score}, game ${Math.min(state.game, state.bestOf)}`;
}

/** Swap the point of view (my wins ↔ their wins) so a re-shared link reads correctly for the friend. */
export function opponentSeries(state: SeriesState): SeriesState {
  return { mode: state.mode, bestOf: state.bestOf, game: state.game, youWins: state.themWins, themWins: state.youWins };
}

/** Encode a state verbatim into the `&series &g &y &t` URL params. */
export function seriesLinkParams(state: SeriesState): SeriesLinkParams {
  return {
    series: state.mode,
    g: String(Math.max(1, Math.floor(state.game))),
    y: String(Math.max(0, Math.floor(state.youWins))),
    t: String(Math.max(0, Math.floor(state.themWins))),
  };
}

/**
 * The params to put in the deep link you SEND the friend so they continue the series: the local state
 * with its perspective flipped (their "you" is our "them").
 */
export function nextSeriesLinkParams(state: SeriesState): SeriesLinkParams {
  return seriesLinkParams(opponentSeries(state));
}

/**
 * A distinct, reproducible seed for game `game` derived from the series' base seed. Lets the three
 * boards differ while both players can derive the SAME board for a given game from the same base seed
 * (so the head-to-head is fair). Pure integer hash — no Math.random, safe to call from the renderer.
 */
export function seriesSeed(baseSeed: number, game: number): number {
  let h = (baseSeed >>> 0) ^ Math.imul(game >>> 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  return (h ^ (h >>> 16)) >>> 0;
}
